import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Button from "../app/Button";
import Dropdown from "../common/Dropdown";
import moment from "moment";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

interface PatientDOBProps {
  title: string;
  description: string;
  value?: string; // format: "YYYY-MM-DD"
  onBack: () => void;
  onNext: (dob: string) => void;
  support_program_key?: string;
}

const PatientDOB: React.FC<PatientDOBProps> = ({
  title,
  description,
  value,
  onBack,
  onNext,
  support_program_key,
}) => {
  const initialDate = value ? new Date(value) : null;

  const [day, setDay] = useState(initialDate?.getDate() || "");
  const [month, setMonth] = useState(
    initialDate ? initialDate.getMonth() + 1 : "",
  );
  const [year, setYear] = useState(initialDate?.getFullYear() || "");

  const isValid = day && month && year;

  useEffect(() => {
    analytics.pageView(SCREEN_NAMES.DOB_CAPTURE_SCREEN);
  }, []);

  const handleContinue = () => {
    if (!isValid) return;

    const formatted = `${year}-${String(month).padStart(2, "0")}-${String(
      day,
    ).padStart(2, "0")}`;

    // Event: DOB_CAPTURED
    analytics.trackEvent(EVENT_NAMES.DOB_CAPTURED, {
      dob: formatted,
      screen_name: SCREEN_NAMES.DOB_CAPTURE_SCREEN,
      support_program_key: support_program_key,
    });
    onNext(formatted);
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  // 18 year check
  const currentYear = moment().year();
  const eighteenYearsAgo = currentYear - 18;
  // replace with current year to get the count from current year till 100 years.
  const years = Array.from({ length: 100 }, (_, i) => eighteenYearsAgo - i);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-4 flex-1 min-h-0 overflow-y-auto">
        <button
          className="flex items-center gap-1 px-4 py-2 -ml-4"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 text-gray-500" />
          <p className="text-sm text-gray-500">Back</p>
        </button>

        <div className="my-4">
          <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
            {title}
          </h1>
          <p className="text-[var(--text-secondary)]">{description}</p>
        </div>

        {/* Dropdowns */}
        <div className="flex gap-3">
          <Dropdown
            name="day"
            options={days.map((d) => ({
              value: d,
              label: d,
            }))}
            value={day}
            onChange={(value) => setDay(Number(value))}
            placeholder="Day"
            width={80}
          />

          <Dropdown
            name="month"
            options={months.map((label, index) => ({
              value: index + 1,
              label,
            }))}
            value={month}
            onChange={(value) => setMonth(Number(value))}
            placeholder="Month"
            fullWidth
          />

          <Dropdown
            name="year"
            options={years.map((y) => ({
              value: y,
              label: y,
            }))}
            value={year}
            onChange={(value) => setYear(Number(value))}
            placeholder="Year"
            width={100}
          />
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 p-4 mt-auto">
        <Button fullWidth onClick={handleContinue} disabled={!isValid}>
          <span className="flex items-center gap-2">
            Continue
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
      </div>
    </div>
  );
};

export default PatientDOB;
